// src/components/PlayerPhoto.jsx
// A player's photo, with a graceful fallback.
//
// Photos come from two places — uploaded ones served from Firebase Storage, and
// the originals bundled with the app. A Storage image can fail for reasons
// outside our control: a slow or filtered connection, a stale cached failure,
// or a file that was removed. Previously that surfaced as the browser's broken
// image icon, which looks like the site is broken.
//
// This falls back to the same initial-letter placeholder used when a player has
// no photo at all, so a failure looks deliberate rather than broken.

import { useEffect, useState } from "react";

function Placeholder({ name, className = "" }) {
  return (
    <div
      className={`w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900 ${className}`}
    >
      <span className="text-4xl font-black text-red-600/30">
        {(name || "?").charAt(0)}
      </span>
    </div>
  );
}

function PlayerPhoto({
  player,
  className = "w-full h-full object-cover",
  eager = false,
}) {
  const src = player?.image || null;
  const [failed, setFailed] = useState(false);

  // A different player (or a freshly uploaded photo) deserves another attempt.
  useEffect(() => setFailed(false), [src]);

  if (!src || failed) return <Placeholder name={player?.name} />;

  return (
    <img
      src={src}
      alt={player?.name || ""}
      className={className}
      style={{ objectPosition: player?.imagePosition || "center top" }}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
}

export default PlayerPhoto;
